import { supabase } from '@/integrations/supabase/client';

// Base configuration
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE;

// Function endpoints
export const FUNCTION_ENDPOINTS = {
  // FCM & Notifications
  FCM_NOTIFY: `${SUPABASE_URL}/functions/v1/fcm-notify`,
  SEND_NOTIFICATION: `${SUPABASE_URL}/functions/v1/send-notification`,
  SEND_ORDER_NOTIFICATION: `${SUPABASE_URL}/functions/v1/send-order-notification`,
  SEND_CUSTOMER_NOTIFICATION: `${SUPABASE_URL}/functions/v1/send-customer-notification`,
  SEND_STAFF_INVITATION: `${SUPABASE_URL}/functions/v1/send-staff-invitation`,
  SEND_STAFF_ACCEPTED_NOTIFICATION: `${SUPABASE_URL}/functions/v1/send-staff-accepted-notification`,
  SEND_DEMO_REQUEST: `${SUPABASE_URL}/functions/v1/send-demo-request`,
  SEND_STOREFRONT_CONTACT: `${SUPABASE_URL}/functions/v1/send-storefront-contact`,
  
  // Orders & Payments
  CREATE_PUBLIC_ORDER: `${SUPABASE_URL}/functions/v1/create-public-order`,
  CREATE_CHECKOUT: `${SUPABASE_URL}/functions/v1/create-checkout`,
  PAYSTACK_INITIALIZE: `${SUPABASE_URL}/functions/v1/paystack-initialize`,
  PAYSTACK_WEBHOOK: `${SUPABASE_URL}/functions/v1/paystack-webhook`,
  PAYSTACK_SUBSCRIPTION: `${SUPABASE_URL}/functions/v1/paystack-subscription`,
  PAYSTACK_SUBSCRIPTION_WEBHOOK: `${SUPABASE_URL}/functions/v1/paystack-subscription-webhook`,
  STRIPE_INITIALIZE: `${SUPABASE_URL}/functions/v1/stripe-initialize`,
  STRIPE_WEBHOOK: `${SUPABASE_URL}/functions/v1/stripe-webhook`,
  
  // Reservations
  CREATE_PUBLIC_RESERVATION: `${SUPABASE_URL}/functions/v1/create-public-reservation`,
  
  // Chat & Messages
  GET_UNREAD_MESSAGES: `${SUPABASE_URL}/functions/v1/get-unread-messages`,
  UPDATE_READ_STATUS: `${SUPABASE_URL}/functions/v1/update-read-status`,
  
  // Activity & Analytics
  CREATE_ACTIVITY_LOG: `${SUPABASE_URL}/functions/v1/create-activity-log`,
  TRACK_BLOG_UPVOTE: `${SUPABASE_URL}/functions/v1/track-blog-upvote`,
  
  // Inventory & Menu
  INVENTORY_TRACKER: `${SUPABASE_URL}/functions/v1/inventory-tracker`,
  UPDATE_MENU_AVAILABILITY: `${SUPABASE_URL}/functions/v1/update-menu-availability`,
  CHECK_MENU_AVAILABILITY: `${SUPABASE_URL}/functions/v1/check-menu-availability`,
  UPDATE_INVENTORY: `${SUPABASE_URL}/functions/v1/update-inventory`,
  
  // User Management
  DELETE_USER: `${SUPABASE_URL}/functions/v1/delete-user`,
  CHECK_SUBSCRIPTION: `${SUPABASE_URL}/functions/v1/check-subscription`,
  
  // Web Push
  GET_VAPID_PUBLIC_KEY: `${SUPABASE_URL}/functions/v1/get-vapid-public-key`,
  SEND_WEB_PUSH_NOTIFICATION: `${SUPABASE_URL}/functions/v1/send-web-push-notification`,
  
  // Tracking
  TRACK_SCAN: `${SUPABASE_URL}/functions/v1/track-scan`,
} as const;

// Default headers for all function calls
export const getDefaultHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
});

// Helper function to get user session
export const getCurrentSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) {
    console.error('Error getting session:', error);
    return null;
  }
  return session;
};

// Helper function to get user
export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) {
    console.error('Error getting user:', error);
    return null;
  }
  return user;
};

// Type for function responses
export interface FunctionResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// Type for function error
export interface FunctionError {
  error: string;
  message: string;
  details?: unknown;
} 